import { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BlockShell from "./BlockShell";
import { API } from "../../App";
import { GivingMapBadge } from "../GivingMapBadge";
import { SpikingSignsBadge } from "../SpikingSignsBadge";

const spotIcon = (logo) => new L.Icon({
  iconUrl: logo || "/logo192.png",
  iconSize: [36, 36], iconAnchor: [18, 18], popupAnchor: [0, -18],
  className: "rounded-full border-2 border-white shadow-md object-cover bg-white",
});

// Teardrop pin with a logo inside — used for giving stations (starflower) and Gem businesses (their logo).
const teardropIcon = (logo) => new L.DivIcon({
  className: "",
  html: `<div style="width:44px;height:44px;background:#fff;border:3px solid #B45309;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,.35);overflow:hidden;display:flex;align-items:center;justify-content:center"><img src="${logo}" alt="" style="width:34px;height:34px;border-radius:50%;object-fit:contain;transform:rotate(45deg);background:#fff"/></div>`,
  iconSize: [44, 44], iconAnchor: [22, 44], popupAnchor: [0, -44],
});


const plainPin = new L.DivIcon({
  className: "",
  html: `<div style="width:30px;height:30px;background:#B45309;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,.35)"></div>`,
  iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
});

// Gem businesses (Hoard contributors) — the layer that used to live on the home Happening Map.
const gemIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#16A085;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.3)"><span style="transform:rotate(45deg);font-size:14px">🎁</span></div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32],
});

const MapBlock = ({ block, festival }) => {
  const cfg = block.config || {};
  const [spots, setSpots] = useState([]);
  const [gems, setGems] = useState([]);

  useEffect(() => {
    if (!cfg.show_gems) return;
    axios.get(`${API}/directory/map-markers`)
      .then((r) => setGems((Array.isArray(r.data) ? r.data : []).filter((b) => b.lat && b.lng)))
      .catch(() => setGems([]));
  }, [cfg.show_gems]);

  useEffect(() => {
    if (!cfg.include_dont_miss) return;
    axios.get(`${API}/dont-miss-spots`).then((r) => {
      const list = r.data.spots || [];
      const filtered = festival ? list.filter((s) => s.festival === festival.slug || !s.festival) : list;
      setSpots(filtered.filter((s) => s.lat && s.lng));
    }).catch(() => setSpots([]));
  }, [cfg.include_dont_miss, festival]);

  const center = cfg.center || festival?.map_center || [47.2529, -122.4443];
  const zoom = cfg.zoom || 15;
  const height = cfg.height || 520;

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-map">
      <BlockShell block={block}>
        <div className="rounded-xl overflow-hidden border border-[#E2E0DC] relative" style={{ height }}>
          <div className="absolute z-[500] bottom-6 left-3 flex flex-wrap items-center gap-2 max-w-[calc(100%-1.5rem)]">
            {cfg.giving_badge && <GivingMapBadge sub={cfg.giving_badge_sub || undefined} to={festival?.slug === "giving-map" ? null : "/give"} testId="map-giving-badge" />}
            <SpikingSignsBadge testId="map-spiking-badge" />
          </div>
          <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {spots.map((s) => (
              <Marker key={s.spot_id} position={[s.lat, s.lng]} icon={spotIcon(s.logo)}>
                <Popup maxWidth={260}>
                  <div className="text-sm">
                    <strong>{s.name}</strong>
                    <div className="text-xs italic text-[#E76F51]">{s.tagline}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
            {gems.map((b) => (
              <Marker key={`gem-${b.business_id}`} position={[b.lat, b.lng]} icon={gemIcon}>
                <Popup>
                  <div className="min-w-[180px] text-sm">
                    <strong>🎁 {b.name}</strong>
                    <div className="text-[11px] text-[#16A085] font-medium mb-2">Gem business — offers for verified donors</div>
                    <a href={`/business/dir/${b.business_id}`} className="inline-block text-xs bg-[#16A085] text-white px-3 py-1.5 rounded no-underline" data-testid={`map-gem-link-${b.business_id}`}>View profile →</a>
                  </div>
                </Popup>
              </Marker>
            ))}
            {(cfg.custom_pins || []).filter((p) => p.lat && p.lng).map((p, i) => (
              <Marker key={i} position={[p.lat, p.lng]} icon={p.icon ? teardropIcon(p.icon) : plainPin}>
                <Popup>
                  <div className="text-sm">
                    <strong>{p.name}</strong>
                    {p.note && <div className="text-xs">{p.note}</div>}
                    {p.link && (/^https?:/i.test(p.link)
                      ? <a href={p.link} target="_blank" rel="noopener noreferrer" className="block text-xs font-bold underline mt-1">{p.link_label || "Open"} →</a>
                      : <a href={p.link} className="block text-xs font-bold underline mt-1">{p.link_label || "Open"} →</a>)}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </BlockShell>
    </section>
  );
};

export default MapBlock;
